import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Shield,
  User,
  Coins,
  Check,
  KeyRound,
  Mail,
} from 'lucide-react';
import { authFetch } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';

interface UserDetail {
  id: string;
  username: string;
  email: string;
  role: 'USER' | 'ADMIN';
  status: 'ACTIVE' | 'SUSPENDED' | 'BANNED';
  coins: number;
  gems: number;
  usdBalanceCents: number;
  level: number;
  xp: number;
  createdAt: string;
  lastLoginAt: string | null;
  mainEventEntries: number;
  transactionCount: number;
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Credit coins form
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [crediting, setCrediting] = useState(false);
  const [creditSuccess, setCreditSuccess] = useState('');
  const [creditError, setCreditError] = useState('');

  // Role/status update
  const [updating, setUpdating] = useState(false);

  // Reset password
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    fetchUser();
  }, [id]);

  async function fetchUser() {
    try {
      setIsLoading(true);
      setError(null);
      const res = await authFetch(`/api/admin/users/${id}`);
      if (!res.ok) throw new Error('Failed to fetch user');
      const data = await res.json();
      setUser(data);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setError('Failed to load user');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreditCoins(e: React.FormEvent) {
    e.preventDefault();
    const amount = parseInt(creditAmount);
    if (!amount || amount <= 0) {
      setCreditError('Enter a positive amount');
      return;
    }

    setCrediting(true);
    setCreditError('');
    setCreditSuccess('');

    try {
      const res = await authFetch(`/api/admin/users/${id}/credit-coins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason: creditReason || undefined }),
      });

      const data = await res.json();
      if (!res.ok) {
        setCreditError(data.error || 'Failed to credit coins');
        return;
      }

      setCreditSuccess(`Credited ${amount.toLocaleString()} coins. New balance: ${data.newBalance?.toLocaleString()}`);
      setCreditAmount('');
      setCreditReason('');

      // Refresh user data
      if (user) {
        setUser({ ...user, coins: data.newBalance });
      }
    } catch {
      setCreditError('Failed to credit coins');
    } finally {
      setCrediting(false);
    }
  }

  async function handleResetPassword() {
    setResetting(true);
    setResetSuccess('');
    setResetError('');

    try {
      const res = await authFetch(`/api/admin/users/${id}/reset-password`, {
        method: 'POST',
      });

      const data = await res.json();
      if (!res.ok) {
        setResetError(data.error || 'Failed to send reset email');
        return;
      }

      setResetSuccess(data.message || 'Password reset email sent');
    } catch {
      setResetError('Failed to send reset email');
    } finally {
      setResetting(false);
    }
  }

  async function handleUpdateUser(field: 'role' | 'status', value: string) {
    if (!user) return;
    setUpdating(true);

    try {
      const res = await authFetch(`/api/admin/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });

      if (res.ok) {
        setUser({ ...user, [field]: value });
      }
    } catch (err) {
      console.error('Failed to update user:', err);
    } finally {
      setUpdating(false);
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatBalance = (usdCents: number) => `$${(usdCents / 100).toFixed(2)}`;

  const inputClass =
    'w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-400">{error || 'User not found'}</p>
        <Link href="/admin/users" className="text-zinc-400 hover:text-white mt-4 inline-block">
          Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/admin/users"
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-zinc-800 flex items-center justify-center">
            {user.role === 'ADMIN' ? (
              <Shield className="h-5 w-5 text-red-400" />
            ) : (
              <User className="h-5 w-5 text-zinc-400" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{user.username}</h1>
            <p className="text-sm text-zinc-400">{user.email}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Balances */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Balances</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-xs text-zinc-400 mb-1">Coins</p>
                <p className="text-xl font-bold text-amber-400">{user.coins.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-xs text-zinc-400 mb-1">Gems</p>
                <p className="text-xl font-bold text-purple-400">{user.gems.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-xs text-zinc-400 mb-1">USD</p>
                <p className="text-xl font-bold text-green-400">{formatBalance(user.usdBalanceCents)}</p>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Details</h2>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-zinc-500">Level</dt>
                <dd className="text-white font-medium">{user.level} ({user.xp.toLocaleString()} XP)</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Event Entries</dt>
                <dd className="text-white font-medium">{user.mainEventEntries}</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Transactions</dt>
                <dd className="text-white font-medium">{user.transactionCount}</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Joined</dt>
                <dd className="text-white font-medium">{formatDate(user.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Last Login</dt>
                <dd className="text-white font-medium">
                  {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-zinc-500">User ID</dt>
                <dd className="text-zinc-400 font-mono text-xs">{user.id}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          {/* Credit Coins */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-400" />
              Credit Coins
            </h2>

            {creditSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm">{creditSuccess}</p>
              </div>
            )}

            {creditError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-sm">{creditError}</p>
              </div>
            )}

            <form onSubmit={handleCreditCoins} className="space-y-3">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Amount</label>
                <input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  min={1}
                  max={1000000}
                  placeholder="e.g., 1000"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="e.g., Beta testing reward"
                  className={inputClass}
                />
              </div>
              <Button type="submit" disabled={crediting} className="w-full">
                {crediting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    Crediting...
                  </>
                ) : (
                  'Credit Coins'
                )}
              </Button>
            </form>
          </div>

          {/* Role & Status */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Role & Status</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Role</label>
                <select
                  value={user.role}
                  onChange={(e) => handleUpdateUser('role', e.target.value)}
                  disabled={updating}
                  className={inputClass}
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Status</label>
                <select
                  value={user.status}
                  onChange={(e) => handleUpdateUser('status', e.target.value)}
                  disabled={updating}
                  className={inputClass}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="BANNED">Banned</option>
                </select>
              </div>
            </div>
          </div>

          {/* Reset Password */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-zinc-400" />
              Password Reset
            </h2>

            {resetSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm">{resetSuccess}</p>
              </div>
            )}

            {resetError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-sm">{resetError}</p>
              </div>
            )}

            <p className="text-sm text-zinc-400 mb-3">
              Send a password reset link to {user.email}
            </p>
            <Button
              onClick={handleResetPassword}
              disabled={resetting}
              variant="outline"
              className="w-full"
            >
              {resetting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-1" />
                  Send Reset Email
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
